'use client'

import { useId, useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

type FaqItem = {
  q: string
  a: ReactNode
}

type Props = {
  items: FaqItem[]
}

export function FaqList({ items }: Props) {
  const baseId = useId()
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set())

  function toggle(index: number) {
    setOpenIndexes((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  return (
    <div className="mx-auto max-w-[820px]">
      {items.map((item, i) => {
        const isOpen = openIndexes.has(i)
        const panelId = `${baseId}-panel-${i}`

        return (
          <div
            key={i}
            className={cn(
              'mb-[14px] overflow-hidden rounded-[16px] border-2 border-ink bg-white',
            )}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(i)}
              className="flex w-full cursor-pointer items-center justify-between gap-4 px-6 py-[22px] text-left font-display text-[18px] font-semibold"
            >
              <span>{item.q}</span>
              <span
                className={cn(
                  'flex h-[28px] w-[28px] flex-none items-center justify-center rounded-[8px] border-2 border-ink bg-paper text-[18px] transition-transform duration-200',
                  isOpen && 'rotate-45 bg-coral text-ink',
                )}
                aria-hidden="true"
              >
                +
              </span>
            </button>

            <div
              id={panelId}
              hidden={!isOpen}
              className="px-6 pb-[22px] text-[15.5px] text-muted"
            >
              {item.a}
            </div>
          </div>
        )
      })}
    </div>
  )
}
